import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { ISeatSelection } from '../../../../core/interfaces/ISeatSelection';
import { ISeatPackageSelection } from '../../../../core/interfaces/ISeatPackageSelection';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { IPackageProduct } from '../../../cart/core/interfaces/IPackageProduct';
import { IShowPriceCategory, IShowPriceCategoryPackage } from '../../../shows/core/interfaces/IShowPriceCategory';
import { PackageSelectionComponent } from '../../components/package-selection/package-selection.component';
import { KioskStore } from '../../core/stores/KioskStore';
import { PackageProductsSelectionModalComponent } from '../package-products-selection-modal/package-products-selection-modal.component';

@Component({
  selector: 'app-ticket-package-selection-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    PackageSelectionComponent,
    PackageProductsSelectionModalComponent,
    CommonModule
  ],
  templateUrl: './ticket-package-selection-modal.component.html',
  styleUrl: './ticket-package-selection-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TicketPackageSelectionModalComponent extends BaseModalComponent<ISeatSelection[]> {

  @ViewChild(PackageProductsSelectionModalComponent) packageProductsSelectionModal: PackageProductsSelectionModalComponent;
  @ViewChild(PackageSelectionComponent) packageSelectionComponent: PackageSelectionComponent;

  @Output() onAbort = new EventEmitter<ISeatSelection[]>();

  public selectedPackage: IShowPriceCategoryPackage | null = null;
  public showPriceCategory: IShowPriceCategory = null;
  public seatPackages: IShowPriceCategoryPackage[] = [];
  public static instance: TicketPackageSelectionModalComponent;
  public currentIndex: number = 0;
  public currentValue: ISeatSelection | null = null;
  public seatSelections: ISeatPackageSelection[] = [];

  private _cb: (seatSelections: ISeatPackageSelection[]) => void;

  public constructor() {
    super();
    TicketPackageSelectionModalComponent.instance = this;
  }

  private readonly _kioskStore = inject(KioskStore);

  public override open(seatSelections: ISeatSelection[], showPriceCategory?: IShowPriceCategory, cb?: (seatSelections: ISeatPackageSelection[]) => void): void {
    super.open(seatSelections);
    this._cb = cb;
    this.showPriceCategory = showPriceCategory;
    this.selectedPackage = null;
    this.currentIndex = 0;

    this.initializeSeatSelections();
    this.initializeCurrentSeat();
    this.cdr.detectChanges();
  }

  private initializeSeatSelections(): void {
    this.seatSelections = this.data.map(seat => ({
      seat,
      package: null,
      selectedProductIds: []
    }));
  }

  public onPackageSelected(pkg: IShowPriceCategoryPackage): void {
    this.selectedPackage = pkg;
  }

  public selectPackage(): void {
    if (this.selectedPackage && this.currentValue) {
      const fPackage = this._kioskStore.kioskProductsAndPackages().package.find(x => x.id === this.selectedPackage.id);
      const uniqueGroupingNrs = [...new Set(fPackage.packageContent.map(x => x.groupingNr))];
      const hasNestedContent = fPackage.packageContent.some(x => !!x.contentPackage);

      if (hasNestedContent || uniqueGroupingNrs.length > 1) {
        this.seatSelections[this.currentIndex].package = this.selectedPackage;
        this.packageProductsSelectionModal.open(fPackage);
        return;
      }

      this.seatSelections[this.currentIndex].package = this.selectedPackage;
      this.seatSelections[this.currentIndex].selectedProductIds = fPackage.packageContent
        .map(x => x.productId)
        .filter((id): id is number => !!id);
      this.proceedToNextSeat();
    }
  }

  public onPackageProductsConfirmed(selectedProducts: IPackageProduct[]): void {
    this.seatSelections[this.currentIndex].selectedProductIds = selectedProducts
      .map(x => Number(x.id))
      .filter(id => !!id);
    this.proceedToNextSeat();
  }

  private proceedToNextSeat(): void {
    this.currentIndex++;

    if (this.currentIndex < this.data.length) {
      this.initializeCurrentSeat();
      this.cdr.detectChanges();
    } else {
      this.finalizeSelections();
    }
  }

  private initializeCurrentSeat(): void {
    if (this.currentIndex < this.data.length) {
      this.currentValue = this.data[this.currentIndex];
      this.seatPackages = this.showPriceCategory?.packages.filter(pkg =>
        pkg.priceAreaId === this.currentValue.priceAreaId
      ) || [];
      this.selectedPackage = null;
      if (this.packageSelectionComponent) {
        this.packageSelectionComponent.selectedPackage = null;
      }

      if (this.seatPackages.length === 1) {
        this.selectedPackage = this.seatPackages[0];
        const fPackage = this._kioskStore.kioskProductsAndPackages().package.find(
          x => x.id === this.selectedPackage.id
        );
        const uniqueGroupingNrs = [...new Set(fPackage?.packageContent?.map(x => x.groupingNr) ?? [])];
        const hasNestedContent = fPackage?.packageContent?.some(x => !!x.contentPackage) ?? false;

        if (hasNestedContent || uniqueGroupingNrs.length > 1) {
          this.seatSelections[this.currentIndex].package = this.selectedPackage;
          setTimeout(() => this.packageProductsSelectionModal.open(fPackage));
          return;
        }

        this.seatSelections[this.currentIndex].package = this.selectedPackage;
        this.seatSelections[this.currentIndex].selectedProductIds =
          fPackage?.packageContent?.map(x => x.productId).filter((id): id is number => !!id) ?? [];
        this.proceedToNextSeat();
      }
    }
  }

  private finalizeSelections(): void {
    const allSeatsHavePackages = this.seatSelections.every(selection => selection.package !== null);

    if (allSeatsHavePackages) {
      this._cb(this.seatSelections);
      this.close();
    }
  }
}
